import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { cn, Filters, type FilterFieldSlot, type FilterValues } from '@evogard/ui';
import {
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  formatDate,
  ticketPriorityLabel,
  ticketStatusLabel,
} from '@evogard/domain';
import type { Priority, Status, TicketFilterInput } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { useDebouncedTerm } from '../../lib/debounce.js';

/**
 * Os recortes de demandas — o estado e a barra, escritos uma vez.
 *
 * A mesa e o relatório precisam filtrar **igual**: a verificação da
 * `demandas-42` é que os totais do relatório sejam os da mesa com os mesmos
 * recortes, e dois blocos de sete seletores copiados divergem no primeiro
 * filtro novo. Quando divergem, o defeito aparece como um relatório que conta
 * diferente da lista — e nada na tela explica por quê.
 *
 * Do lado do servidor a mesma decisão já está tomada: `ticketFilterQuery` no
 * contrato e `conditionsOf` no serviço. Este arquivo é a terceira ponta.
 *
 * ## Seis campos são vários, dois são um só
 *
 * Situação, categoria, departamento, prioridade e as duas pessoas (quem
 * aderiu, quem abriu) aceitam VÁRIAS opções — o `MultiSelect` da marca, com
 * busca e ordenação. Período e texto continuam de um valor só: não há "várias
 * datas" nem "vários textos" para buscar.
 *
 * ## O que mudou em 17/09: sete seletores viraram um botão
 *
 * Eles moravam num `FilterCard` entre o título e a tabela, e cada mudança
 * disparava uma consulta — montar um recorte de quatro campos custava quatro
 * requisições e três listas intermediárias que ninguém queria ver. Agora são o
 * `Filters` de `@evogard/ui`: o painel guarda o rascunho, e a tela só é
 * consultada quando alguém confirma.
 *
 * **A busca não foi junto**, e é a única exceção: ela continua fora do painel,
 * filtrando enquanto se digita. É o filtro cujo resultado a pessoa usa para
 * decidir o que digitar em seguida — atrás de um botão de confirmar ele
 * deixaria de servir para isso.
 */

export interface FilterState {
  situacao: string[];
  categoryId: string[];
  assigneeId: string[];
  requesterUserId: string[];
  departmentId: string[];
  priority: string[];
  openedFrom: string;
  openedTo: string;
  search: string;
  /** o toggle da barra: `''` é nenhum aceso, que é a mesa inteira do escopo */
  mine: '' | 'comigo' | 'meu_setor' | 'abri';
}

export interface TicketFilters {
  state: FilterState;
  /**
   * O texto da busca, que é o único filtro que vale ao digitar.
   *
   * Ele não passa pelo painel: fica na barra acima da tabela, e só chega ao
   * endereço depois de a digitação descansar.
   */
  setSearch: (value: string) => void;
  /**
   * O recorte do PAINEL — tudo menos a busca, de uma vez só.
   *
   * Recebe o mapa completo das chaves que o painel declara, com as vazias como
   * `''` ou `[]`: uma chave que chegasse ausente seria uma chave que a tela não
   * sabe apagar, e o filtro anterior sobreviveria ao "Limpar".
   */
  apply: (values: FilterValues) => void;
  /** O recorte do painel como ele está valendo — o que o `Filters` lê. */
  values: FilterValues;
  /**
   * O que vai à API, já sem as listas vazias — e ainda como TEXTO separado
   * por vírgula, a forma que `ticketFilterQuery` espera receber. Quem lê essa
   * vírgula e a transforma em lista é o contrato, no servidor
   * (`TicketFilterInput` é o tipo de ANTES do `.transform`; `TicketFilter`, em
   * `conditionsOf`, é o de depois).
   */
  query: TicketFilterInput;
  /**
   * O termo que FOI consultado — já esperada a digitação parar.
   *
   * Sai daqui e não de `state.search` porque o resumo do recorte é o que vai
   * para o papel: com o texto cru, a folha diria `Busca: "financeir"` sobre uma
   * contagem feita com `financ`, e um relatório que descreve errado o próprio
   * recorte é a única coisa pior do que não ter resumo.
   */
  term: string;
  /** a parte da `queryKey` que muda quando o recorte muda */
  key: string[];
  /** alguém filtrou alguma coisa — é o que decide a frase do vazio */
  active: boolean;

  /** o toggle da barra, como está valendo */
  mine: FilterState['mine'];
  /**
   * Liga o recorte — e clicar no que já está aceso DESLIGA.
   *
   * É o que substitui o botão "Todas" que a mesa não tem: sem um terceiro
   * estado visível, desligar precisa ser o mesmo gesto de ligar, senão a mesa
   * inteira deixa de ser alcançável para quem não pensa em mexer nos filtros.
   */
  toChoose: (value: FilterState['mine']) => void;
}

/**
 * O estado dos filtros, com a busca já esperando a digitação parar.
 *
 * O termo cru entrava na chave da consulta e na requisição: um título de vinte
 * caracteres eram vinte `GET`, vinte entradas de cache e, em rede ruim,
 * respostas chegando fora da ordem em que foram pedidas.
 */
/**
 * O recorte, como ele viaja no endereço.
 *
 * As listas vão como texto separado por vírgula, que é **a mesma forma** que a
 * API recebe (`query`, logo abaixo) — uma conversão a menos, e um endereço que
 * se lê. Todos os campos são opcionais: `/tickets` sem nada é a mesa inteira.
 *
 * `.catch(undefined)` em cada um: endereço é coisa que gente cola, edita e
 * encurta. Um `?priority=` com lixo dentro não pode derrubar a tela — ele cai
 * para "sem recorte", que é o pior caso honesto.
 */
export const ticketFilterSearch = z.object({
  situacao: z.string().optional().catch(undefined),
  categoryId: z.string().optional().catch(undefined),
  assigneeId: z.string().optional().catch(undefined),
  requesterUserId: z.string().optional().catch(undefined),
  departmentId: z.string().optional().catch(undefined),
  priority: z.string().optional().catch(undefined),
  openedFrom: z.string().optional().catch(undefined),
  openedTo: z.string().optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  /*
   * `z.string()` e não `z.enum` — o endereço é coisa que gente cola e edita, e
   * `.catch(undefined)` já devolve a mesa inteira em vez de quebrar. Quem
   * valida o vocabulário é o contrato, na borda da API. O `state` abaixo
   * normaliza para a união antes de qualquer coisa usá-lo.
   */
  mine: z.string().optional().catch(undefined),
});

export type TicketFilterSearch = z.infer<typeof ticketFilterSearch>;

/** `'a,b'` → `['a', 'b']`, e vazio → `[]`. */
const listOf = (value: string | undefined): string[] =>
  value ? value.split(',').filter(Boolean) : [];

/**
 * O estado dos filtros — **no ENDEREÇO**, desde 17/09, e não mais em `useState`.
 *
 * ## O que a mudança conserta
 *
 * O recorte morava em `useState`, e sair da mesa o apagava. Isso passou a
 * custar caro no dia em que o resumo rápido saiu: abrir uma demanda deixou de
 * ser um modal por cima e virou navegação, e com o estado local a volta trazia
 * a mesa zerada — sete filtros para refazer a cada demanda aberta. No endereço,
 * o voltar do navegador devolve a mesa exatamente como ela estava.
 *
 * De quebra o recorte passou a ser colável: "olha estas 12 demandas" é um
 * endereço, e não uma receita de quais seletores mexer.
 *
 * ## `replace`, e não `push`
 *
 * Marcar um filtro **não** é um passo de navegação. Com `push`, uma sessão de
 * triagem enche o histórico e voltar da demanda exigiria desfazer filtro por
 * filtro antes de sair da mesa. Com `replace`, o histórico guarda "a mesa" e
 * "a demanda", que é o que a pessoa percorreu.
 *
 * ## O texto é o único que NÃO viaja a cada tecla
 *
 * Ele fica em estado local para o campo responder na hora, e só chega ao
 * endereço já descansado (`useDebouncedTerm`). Escrever no endereço a cada
 * tecla passaria toda digitação por uma volta no roteador — e o campo de busca
 * desta tela já é o que tem a instabilidade conhecida em
 * `internal.tickets.spec.ts`.
 *
 * `strict: false` porque o mesmo gancho serve DUAS rotas — a mesa e o
 * relatório —, e as duas declaram `ticketFilterSearch`.
 */
export function useTicketFilters(): TicketFilters {
  const search = useSearch({ strict: false }) as TicketFilterSearch;
  const navigate = useNavigate();

  const state: FilterState = {
    situacao: listOf(search.situacao),
    categoryId: listOf(search.categoryId),
    assigneeId: listOf(search.assigneeId),
    requesterUserId: listOf(search.requesterUserId),
    departmentId: listOf(search.departmentId),
    priority: listOf(search.priority),
    openedFrom: search.openedFrom ?? '',
    openedTo: search.openedTo ?? '',
    /* o campo mostra o que está sendo digitado; o endereço, o que já descansou */
    search: search.search ?? '',
    mine:
      search.mine === 'comigo' || search.mine === 'meu_setor' || search.mine === 'abri'
        ? search.mine
        : '',
  };

  const write = (patch: Partial<TicketFilterSearch>) => {
    void navigate({
      to: '.',
      search: (current: Record<string, unknown>) => ({ ...current, ...patch }),
      replace: true,
    });
  };

  const [typed, setTyped] = useState(state.search);
  const term = useDebouncedTerm(typed).trim();

  /*
   * O termo descansado sobe para o endereço, e o endereço manda de volta.
   *
   * A guarda `term !== (search.search ?? '')` é o que impede o laço: sem ela,
   * escrever no endereço dispara a releitura, que dispara a escrita.
   */
  useEffect(() => {
    if (term !== (search.search ?? '')) write({ search: term || undefined });
  }, [term]);

  return {
    state: { ...state, search: typed },
    setSearch: setTyped,
    /*
     * Uma escrita só para o recorte inteiro, e não uma por campo.
     *
     * Com `replace: true` no roteador, sete escritas seguidas são sete voltas
     * no roteador para chegar ao mesmo endereço — e cada uma delas reconsulta.
     * O painel já junta tudo antes de confirmar; aqui só se traduz.
     */
    apply: (next) => {
      const patch: Record<string, string | undefined> = {};
      for (const [field, value] of Object.entries(next)) {
        const text = Array.isArray(value) ? value.join(',') : value;
        /* `undefined` tira a chave do endereço; `''` a deixaria lá, vazia */
        patch[field] = text || undefined;
      }
      write(patch);
    },
    /*
     * O texto NÃO entra aqui. Ele é o filtro da barra, e o painel que o
     * recebesse o zeraria no "Limpar" — apagando, no meio da digitação, o que
     * a pessoa está olhando.
     */
    values: {
      situacao: state.situacao,
      categoryId: state.categoryId,
      assigneeId: state.assigneeId,
      requesterUserId: state.requesterUserId,
      departmentId: state.departmentId,
      priority: state.priority,
      openedFrom: state.openedFrom,
      openedTo: state.openedTo,
    },
    query: {
      situacao: state.situacao.length ? state.situacao.join(',') : undefined,
      categoryId: state.categoryId.length ? state.categoryId.join(',') : undefined,
      assigneeId: state.assigneeId.length ? state.assigneeId.join(',') : undefined,
      requesterUserId: state.requesterUserId.length
        ? state.requesterUserId.join(',')
        : undefined,
      departmentId: state.departmentId.length ? state.departmentId.join(',') : undefined,
      priority: state.priority.length ? state.priority.join(',') : undefined,
      openedFrom: state.openedFrom || undefined,
      openedTo: state.openedTo || undefined,
      search: term || undefined,
      mine: state.mine || undefined,
    },
    term,
    key: [
      state.situacao.join(','),
      state.categoryId.join(','),
      state.assigneeId.join(','),
      state.requesterUserId.join(','),
      state.departmentId.join(','),
      state.priority.join(','),
      state.openedFrom,
      state.openedTo,
      term,
      state.mine,
    ],
    mine: state.mine,
    /* "Todas" é o valor vazio, e tem botão próprio: escolher é sempre acender */
    toChoose: (value) => write({ mine: value || undefined }),
    active: Boolean(
      state.situacao.length ||
      state.categoryId.length ||
      state.assigneeId.length ||
      state.requesterUserId.length ||
      state.departmentId.length ||
      state.priority.length ||
      state.openedFrom ||
      state.openedTo ||
      term ||
      state.mine,
    ),
  };
}

/* ------------------------------------------------------------------------- *
 * A barra de recortes — quatro vistas da mesma mesa
 * ------------------------------------------------------------------------- */

/**
 * Os quatro botões, sempre com um aceso (23/09/2026). "Todas" era o estado sem
 * botão nenhum aceso — invisível —, e quem acendia "Do meu setor" deixava de
 * ver a demanda em que era solicitante sem entender por quê.
 *
 * "Minhas solicitações" é o antigo "Que eu abri", e passou a incluir as em que
 * a pessoa é o SOLICITANTE: as duas respondem "o que eu pedi".
 */
const MINE_TABS = [
  { value: '' as const, label: 'Todas' },
  { value: 'comigo' as const, label: 'Comigo' },
  { value: 'meu_setor' as const, label: 'Do meu setor' },
  { value: 'abri' as const, label: 'Minhas solicitações' },
];

/**
 * Os dois recortes, como dois botões que alternam.
 *
 * ## As decisões que o portal já pagou, e que valem aqui
 *
 * **Botões, não links.** O recorte não é um lugar: é a mesma tela mostrando
 * outra coisa. Como link, cada toque empilharia uma entrada no histórico e o
 * "voltar" do navegador viraria "desfazer o filtro", que não é o que ele
 * promete.
 *
 * **`aria-pressed`, não `role="tab"`.** `tab` promete um `tabpanel` associado e
 * navegação por setas. Aqui não há painel: há uma lista que muda de conteúdo.
 *
 * **44px onde há dedo** (`pointer-coarse:min-h-11`), cobrado por
 * `internal.touch.spec.ts`.
 *
 * ## O que é diferente daqui
 *
 * O estado vai para o ENDEREÇO, e não para um `useState`. A mesa já leva todos
 * os recortes dela para lá — recarregar a página não pode desfazer o que a
 * pessoa escolheu, e o endereço é o que ela manda para um colega.
 *
 * Até 22/09 clicar no botão aceso o desligava, e "nenhum aceso" era a mesa
 * inteira — um estado sem nome. Agora ele é "Todas", e há sempre um aceso.
 *
 * No telefone os quatro não cabem ao lado da busca: a faixa rola de lado
 * dentro dela mesma (`overflow-x-auto`), e a página não.
 */
export function TicketMineToggle({ filters }: { filters: TicketFilters }) {
  return (
    <div
      role="group"
      aria-label="Recorte da mesa"
      className="flex h-11 max-w-full items-center gap-1 overflow-x-auto rounded-borda bg-superficie-suave p-1"
    >
      {MINE_TABS.map((tab) => {
        const active = filters.mine === tab.value;
        return (
          <button
            key={tab.value}
            type="button"
            aria-pressed={active}
            onClick={() => filters.toChoose(tab.value)}
            className={cn(
              'h-full shrink-0 whitespace-nowrap rounded-pequeno px-3 text-sm peso-controle transition-colors',
              /*
               * Sem sombra no aceso, e é a MESMA decisão que o segmento do
               * portal já tinha escrito: sombra marca sobreposição real — menu,
               * popover, diálogo —, e aqui a superfície clara sobre o trilho
               * acinzentado já separa os dois na mesma camada. O trabalho é da
               * cor, não da profundidade.
               *
               * Este botão nasceu com `shadow-cartao` em 17/09, e a régua
               * `cartao-a-mao` — que chegou em 18/09 — o pegou: a sombra do
               * cartão num segmento é a casca de cartão escrita à mão num lugar
               * que não é cartão.
               */
              active ? 'bg-superficie text-texto' : 'text-texto-suave hover:text-texto',
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * O botão de filtros da mesa — e o painel que ele abre.
 *
 * Três listas vêm de **outros módulos** (categorias, setores e pessoas), e cada
 * uma só é buscada com a permissão correspondente. Sem ela o campo **some** em
 * vez de nascer vazio: um seletor com só "Todos" dentro é a tela dizendo que a
 * base não tem departamento nenhum, quando o que houve foi um 403 na lista.
 * Por isso os campos entram na lista por `&&` — o `Filters` aceita entrada
 * falsa e a descarta.
 *
 * "Responsável aderido" e "Aberta por" leem a MESMA lista de
 * pessoas, com a mesma chave de consulta: é uma requisição para os dois campos.
 * A abertura só busca entre usuários internos — o associado que abre pelo
 * portal continua na mesa, só não é encontrável por este filtro (ver o
 * comentário de `requesterUserId` em `ticketFilterQuery`).
 */
export function TicketFilterButton({
  filters,
  withoutDepartment = false,
}: {
  filters: TicketFilters;
  /**
   * Sem o campo de departamento — o dashboard do Relatório (`demandas-47`) tem
   * o seletor de setor próprio, e dois lugares escolhendo o setor discordariam.
   */
  withoutDepartment?: boolean;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  const canSeeCategories = can(context, 'tickets.categories', 'ver');
  const canSeeDepartments = can(context, 'platform.departments', 'ver');
  const canSeePeople = can(context, 'platform.users', 'ver');

  const categories = useQuery({
    queryKey: ['demandas', 'categorias', tenantId],
    enabled: canSeeCategories,
    queryFn: async () => {
      const r = await api.tickets.listCategories({ query: {} });
      return ok(r, 200, 'Não foi possível carregar as categorias.');
    },
  });

  const departments = useQuery({
    queryKey: ['plataforma', 'departamentos', tenantId, false],
    enabled: canSeeDepartments,
    queryFn: async () => {
      const r = await api.platform.listDepartments({ query: {} });
      return ok(r, 200, 'Não foi possível carregar os departamentos.');
    },
  });

  const people = useQuery({
    /*
     * `'todas-as-pessoas'` e não `''`: com a string vazia, esta chave era
     * IDÊNTICA à da tela de usuários com a busca em branco (`UsersScreen.tsx`)
     * — mesma entrada de cache, dois `queryFn` diferentes. Quem tinha aberto
     * `/usuarios` antes achava aqui a página de 50 linhas, com `isLoading` já
     * `false`: o seletor renderizava habilitado e incompleto.
     *
     * O prefixo `['plataforma', 'usuarios']` fica: é ele que o
     * `invalidateQueries` da tela de usuários alcança.
     */
    queryKey: ['plataforma', 'usuarios', tenantId, 'todas-as-pessoas'],
    enabled: canSeePeople,
    queryFn: async () => {
      const r = await api.platform.listUsers({ query: { page: 1, perPage: 200 } });
      return ok(r, 200, 'Não foi possível carregar as pessoas.');
    },
  });

  const peopleOptions = (people.data?.items ?? []).map((person) => ({
    value: person.id,
    label: person.nome,
  }));

  const fields: FilterFieldSlot[] = [
    /*
     * O período é de ABERTURA e é inclusivo dos dois lados — "de 01/08 a 31/08"
     * traz o dia 31 inteiro. Quem faz essa conta é o servidor, em
     * `conditionsOf`; daqui viaja `AAAA-MM-DD`, que é o que o `date` dá.
     *
     * Ele vem primeiro e ocupa as duas colunas, como a "Data de Criação" da
     * referência: é o recorte que muda o significado de todos os outros.
     */
    {
      type: 'intervalo',
      label: 'Aberto em',
      from: 'openedFrom',
      to: 'openedTo',
    },
    {
      type: 'multipla',
      name: 'situacao',
      label: 'Situação',
      placeholder: 'Todas',
      options: TICKET_STATUSES.map((s) => ({ value: s, label: ticketStatusLabel(s) })),
    },
    {
      type: 'multipla',
      name: 'priority',
      label: 'Prioridade',
      placeholder: 'Todas',
      options: TICKET_PRIORITIES.map((p) => ({
        value: p,
        label: ticketPriorityLabel(p),
      })),
    },
    canSeeCategories && {
      type: 'multipla',
      name: 'categoryId',
      label: 'Categoria',
      placeholder: 'Todas',
      loading: categories.isLoading,
      options: (categories.data ?? []).map((category) => ({
        value: String(category.id),
        label: category.nome,
      })),
    },
    canSeeDepartments &&
      !withoutDepartment && {
        type: 'multipla',
        name: 'departmentId',
        label: 'Departamento',
        placeholder: 'Todos',
        loading: departments.isLoading,
        options: (departments.data ?? []).map((d) => ({ value: d.id, label: d.nome })),
      },
    canSeePeople && {
      type: 'multipla',
      name: 'assigneeId',
      label: 'Responsável aderido',
      placeholder: 'Todos',
      loading: people.isLoading,
      options: peopleOptions,
    },
    canSeePeople && {
      type: 'multipla',
      name: 'requesterUserId',
      label: 'Aberto por',
      placeholder: 'Todos',
      loading: people.isLoading,
      options: peopleOptions,
    },
  ];

  return <Filters values={filters.values} onApply={filters.apply} fields={fields} />;
}

/** O que a busca da barra precisa saber — a mesma forma nas duas telas. */
export function ticketSearch(filters: TicketFilters) {
  return {
    label: 'Pesquisar tickets',
    placeholder: 'Código ou Título',
    value: filters.state.search,
    onChange: filters.setSearch,
  };
}

/**
 * O recorte, dito por extenso — e é a parte que vai para o papel.
 *
 * A barra de seletores é navegação: no papel ela some, e sem esta frase a folha
 * impressa seria uma tabela de números sem dizer números de quê. É o que o
 * relatório do `ticketgard` faz na caixa cinza do topo ("Período selecionado:
 * X a Y"), e é o mínimo para uma folha impressa continuar verdadeira depois de
 * sair da tela.
 *
 * As três listas são as MESMAS consultas da barra, com as mesmas chaves — o
 * React Query as compartilha, e a tela faz uma requisição de cada, não duas.
 * Enquanto uma delas está no ar, o filtro aparece pelo id e não pelo nome: é
 * feio por dois quadros e é honesto, porque inventar o nome antes de saber é o
 * que esta casa não faz.
 */
export function TicketFilterSummary({ filters }: { filters: TicketFilters }) {
  const { data: context } = useSessionContext();
  const { state } = filters;
  const tenantId = context?.currentTenant.id;

  const categories = useQuery({
    queryKey: ['demandas', 'categorias', tenantId],
    enabled: can(context, 'tickets.categories', 'ver'),
    queryFn: async () => {
      const r = await api.tickets.listCategories({ query: {} });
      return ok(r, 200, 'Não foi possível carregar as categorias.');
    },
  });

  const departments = useQuery({
    queryKey: ['plataforma', 'departamentos', tenantId, false],
    enabled: can(context, 'platform.departments', 'ver'),
    queryFn: async () => {
      const r = await api.platform.listDepartments({ query: {} });
      return ok(r, 200, 'Não foi possível carregar os departamentos.');
    },
  });

  const people = useQuery({
    queryKey: ['plataforma', 'usuarios', tenantId, 'todas-as-pessoas'],
    enabled: can(context, 'platform.users', 'ver'),
    queryFn: async () => {
      const r = await api.platform.listUsers({ query: { page: 1, perPage: 200 } });
      return ok(r, 200, 'Não foi possível carregar as pessoas.');
    },
  });

  /*
   * "A base inteira" deixou de ser verdade em 17/09/2026.
   *
   * Quem não tem `tickets.tickets:ver_qualquer` enxerga o próprio setor, o que
   * abriu e o que responde — e este texto vai para o PAPEL, no cabeçalho do
   * relatório impresso. Uma folha que descreve errado o próprio recorte é a
   * única coisa pior do que não ter resumo, e ela sobrevive à tela.
   */
  if (!filters.active) {
    return (
      <span className="text-texto-suave">
        {can(context, 'tickets.tickets', 'ver_qualquer')
          ? 'Sem recorte: a base inteira, desde o começo.'
          : 'Sem recorte: tudo o que você acompanha — o seu setor, o que você abriu e o que responde.'}
      </span>
    );
  }

  const personName = (id: string) =>
    people.data?.items.find((person) => person.id === id)?.nome ?? id;

  const pieces: string[] = [];
  if (filters.term) pieces.push(`Busca: "${filters.term}"`);
  if (state.situacao.length) {
    pieces.push(
      `Situação: ${state.situacao.map((s) => ticketStatusLabel(s as Status)).join(', ')}`,
    );
  }
  if (state.priority.length) {
    pieces.push(
      `Prioridade: ${state.priority.map((p) => ticketPriorityLabel(p as Priority)).join(', ')}`,
    );
  }
  if (state.categoryId.length) {
    const names = state.categoryId.map(
      (id) => categories.data?.find((cat) => String(cat.id) === id)?.nome ?? id,
    );
    pieces.push(`Categoria: ${names.join(', ')}`);
  }
  if (state.departmentId.length) {
    const names = state.departmentId.map(
      (id) => departments.data?.find((d) => d.id === id)?.nome ?? id,
    );
    pieces.push(`Departamento: ${names.join(', ')}`);
  }
  if (state.assigneeId.length) {
    pieces.push(`Responsável aderido: ${state.assigneeId.map(personName).join(', ')}`);
  }
  if (state.requesterUserId.length) {
    pieces.push(`Aberto por: ${state.requesterUserId.map(personName).join(', ')}`);
  }
  /*
   * O período é dito inteiro mesmo com um extremo só: "aberta a partir de" e
   * "aberta até" são recortes diferentes de "aberta entre", e uma frase que
   * dissesse só a data faria as três parecerem a mesma coisa no papel.
   */
  if (state.openedFrom && state.openedTo) {
    pieces.push(
      `Aberto de ${formatDate(state.openedFrom)} a ${formatDate(state.openedTo)}`,
    );
  } else if (state.openedFrom) {
    pieces.push(`Aberto a partir de ${formatDate(state.openedFrom)}`);
  } else if (state.openedTo) {
    pieces.push(`Aberto até ${formatDate(state.openedTo)}`);
  }

  return <>{pieces.join(' · ')}</>;
}
